/**
 * Cliente HTTP da configuração comercial do SignCheck Admin (E51.F4). // ux-neutrality:allow doc-comment
 *
 * Três endpoints sob /api/plataforma: GET /bancos, GET /precificacao?banco=, // ux-neutrality:allow doc-comment
 * POST /precificacao. Espelha os contratos do backend sem recalcular preço nem // ux-neutrality:allow doc-comment
 * vigência. Erros viram `PrecificacaoError.reason` (estável para a UI). // ux-neutrality:allow doc-comment
 */
import axios, { AxiosError, type AxiosInstance } from 'axios';
import { ptBR } from '@/i18n/pt-BR';

export interface BancoResumo {
  id: string;
  nome: string;
}

export interface PrecificacaoFaixa {
  id: string;
  tabela_id: string;
  ordem: number;
  volume_inicio: number;
  volume_fim: number | null;
  valor_exame_centavos: number;
  valor_certificacao_agregado_centavos: number;
}

export interface PrecificacaoTabela {
  id: string;
  banco_id: string;
  vigencia_inicio: string;
  vigencia_fim: string;
  moeda: string;
  criterio: string;
  criado_em: string;
}

export interface PrecificacaoVersao {
  tabela: PrecificacaoTabela;
  faixas: PrecificacaoFaixa[];
  vigente: boolean;
}

export interface PrecificacaoHistorico {
  banco_id: string;
  referencia: string;
  versoes: PrecificacaoVersao[];
}

export interface FaixaInput {
  ordem: number;
  volume_inicio: number;
  volume_fim: number | null;
  valor_exame_centavos: number;
  valor_certificacao_agregado_centavos: number;
}

export interface NovaVersaoPayload {
  banco_id: string;
  vigencia_inicio: string;
  vigencia_fim: string;
  faixas: FaixaInput[];
}

export interface PrecificacaoTabelaComFaixas {
  tabela: PrecificacaoTabela;
  faixas: PrecificacaoFaixa[];
}

export type PrecificacaoErrorReason =
  | 'validation'
  | 'conflict'
  | 'not_found'
  | 'unauthorized'
  | 'network'
  | 'unknown';

export class PrecificacaoError extends Error {
  public readonly reason: PrecificacaoErrorReason;

  constructor(reason: PrecificacaoErrorReason, message?: string) {
    super(message ?? reason);
    this.reason = reason;
    this.name = 'PrecificacaoError';
  }
}

const _STATUS_TO_REASON: Record<number, PrecificacaoErrorReason> = {
  401: 'unauthorized',
  403: 'unauthorized',
  404: 'not_found',
  409: 'conflict',
  422: 'validation',
};

function _reasonFor(err: AxiosError): PrecificacaoErrorReason {
  const status = err.response?.status;
  if (status !== undefined) {
    const byStatus = _STATUS_TO_REASON[status];
    if (byStatus !== undefined) return byStatus;
  }
  if (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED') {
    return 'network';
  }
  return 'unknown';
}

function _classify(err: unknown): PrecificacaoError {
  if (!axios.isAxiosError(err)) {
    return new PrecificacaoError('unknown');
  }
  return new PrecificacaoError(_reasonFor(err as AxiosError));
}

export async function listBancos(client: AxiosInstance): Promise<BancoResumo[]> {
  try {
    const { data } = await client.get<BancoResumo[]>('/plataforma/bancos');
    return data;
  } catch (err) {
    throw _classify(err);
  }
}

export async function getHistorico(
  client: AxiosInstance,
  bancoId: string,
): Promise<PrecificacaoHistorico> {
  try {
    const { data } = await client.get<PrecificacaoHistorico>('/plataforma/precificacao', {
      params: { banco: bancoId },
    });
    return data;
  } catch (err) {
    throw _classify(err);
  }
}

export async function criarVersao(
  client: AxiosInstance,
  payload: NovaVersaoPayload,
): Promise<PrecificacaoTabelaComFaixas> {
  try {
    const { data } = await client.post<PrecificacaoTabelaComFaixas>(
      '/plataforma/precificacao',
      payload,
    );
    return data;
  } catch (err) {
    throw _classify(err);
  }
}

const _moeda = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

/** Centavos inteiros do backend → texto em reais; `null` vira o marcador do catálogo. */
export function formatarCentavos(valor: number | null): string {
  return valor === null ? ptBR.precificacao.valorAusente : _moeda.format(valor / 100);
}

/** Data civil ISO (`AAAA-MM-DD`) → pt-BR, ancorada em UTC para não deslizar de fuso. */
export function formatarDataVigencia(iso: string): string {
  const data = new Date(iso.includes('T') ? iso : `${iso}T00:00:00Z`);
  if (Number.isNaN(data.getTime())) return iso;
  return data.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
}
